"use client";

import { useFullscreenContext } from "@/contexts/fullscreen-context";
import { useIsMobile } from "@/hooks/use-mobile";
import { useFullscreen } from "@/hooks/useFullscreen";
import {
  captureGameAnalyticsEvent,
  createGameAnalyticsRuntimeState,
  reportRejectedAnalyticsEvent,
} from "@/lib/analytics/game-events";
import { GAME_THEATER_PLAYER_BG_CLASS } from "@/lib/constants/layout";
import { Z_INDEX_CLASSES } from "@/lib/constants/z-index";
import { usePostHog } from "posthog-js/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { GamePlayerLoadingGate } from "./game-player-loading-gate";

const EMPTY_ORIGINS: string[] = [];

interface GamePlayerProps {
  gameId: string;
  gameName: string;
  gameSlug: string;
  url: string;
  title: string;
  assetId?: string;
  assetType?: string;
  onFullscreenToggle?: (isFullscreen: boolean) => void;
  mobileAspectRatio?: string; // e.g., "3:4"
  platformEventTrackingEnabled?: boolean;
  trustedAnalyticsOrigins?: string[];
  versionId?: string;
  /** Fills parent height (game page theater layout) instead of fixed aspect ratio. */
  fillContainer?: boolean;
}

export function GamePlayer({
  gameId,
  gameName,
  gameSlug,
  url,
  title,
  assetId,
  assetType,
  onFullscreenToggle,
  mobileAspectRatio,
  platformEventTrackingEnabled = false,
  trustedAnalyticsOrigins = EMPTY_ORIGINS,
  versionId,
  fillContainer = false,
}: GamePlayerProps) {
  const iframeContainerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const isMobile = useIsMobile();
  const posthog = usePostHog();
  const analyticsRuntimeState = useMemo(
    () => createGameAnalyticsRuntimeState(),
    [assetId, gameId, versionId]
  );
  const { registerToggleFullscreen, registerFullscreenState } = useFullscreenContext();

  // Use the fullscreen hook
  const { isFullscreen, isPseudoFullscreen, toggleFullscreen } = useFullscreen({
    containerRef: iframeContainerRef,
    isMobile,
    onFullscreenToggle,
  });

  // Register toggle function and state with context
  useEffect(() => {
    registerToggleFullscreen(toggleFullscreen);
  }, [toggleFullscreen, registerToggleFullscreen]);

  useEffect(() => {
    registerFullscreenState(isFullscreen, isPseudoFullscreen);
  }, [isFullscreen, isPseudoFullscreen, registerFullscreenState]);

  // Convert A:B to A/B for CSS aspect-ratio
  const cssAspectRatio = (mobileAspectRatio || "3:4").replace(":", "/");

  useEffect(() => {
    setIframeLoaded(false);
  }, [url]);

  useEffect(() => {
    const trustedOrigins = new Set(
      [url, ...trustedAnalyticsOrigins]
        .map((candidate) => {
          if (!candidate) return null;
          try {
            return new URL(candidate).origin;
          } catch {
            return null;
          }
        })
        .filter((origin): origin is string => Boolean(origin))
    );

    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      if (trustedOrigins.size === 0) {
        return;
      }

      if (!trustedOrigins.has(event.origin)) {
        reportRejectedAnalyticsEvent("origin_mismatch", {
          trustedOrigins: Array.from(trustedOrigins),
          actualOrigin: event.origin,
          gameSlug,
        });
        return;
      }

      captureGameAnalyticsEvent(
        event.data,
        {
          enabled: platformEventTrackingEnabled,
          game_id: gameId,
          game_slug: gameSlug,
          game_name: gameName,
          source: "web_iframe",
          asset_id: assetId,
          asset_type: assetType,
          version_id: versionId,
          page_path: window.location.pathname,
        },
        posthog?.capture?.bind(posthog),
        analyticsRuntimeState,
        { requireTypeEnvelope: true }
      );
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [
    assetId,
    assetType,
    analyticsRuntimeState,
    gameId,
    gameName,
    gameSlug,
    platformEventTrackingEnabled,
    posthog,
    trustedAnalyticsOrigins,
    url,
    versionId,
  ]);

  const useAspectRatio = !fillContainer && !isPseudoFullscreen;
  const aspectStyle = useAspectRatio && isMobile ? { aspectRatio: cssAspectRatio } : undefined;

  return (
    <div
      ref={iframeContainerRef}
      className={`relative w-full overflow-hidden ${
        fillContainer || isPseudoFullscreen
          ? `h-full rounded-none ${GAME_THEATER_PLAYER_BG_CLASS}`
          : !isMobile
            ? "aspect-video rounded-3xl"
            : "rounded-none"
      } ${isPseudoFullscreen ? `fixed inset-0 ${Z_INDEX_CLASSES.FULLSCREEN_CONTAINER}` : ""}`}
      style={
        isPseudoFullscreen
          ? { width: "100vw", height: "100dvh", aspectRatio: "unset" }
          : aspectStyle
      }
    >
      <GamePlayerLoadingGate
        loading={!iframeLoaded}
        isMobile={isMobile}
        className="absolute inset-0"
      >
        <iframe
          ref={iframeRef}
          src={url}
          title={title}
          className="absolute inset-0 h-full w-full border-0"
          allowFullScreen
          onLoad={() => setIframeLoaded(true)}
        />
      </GamePlayerLoadingGate>
    </div>
  );
}
